import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Globe, FileText, Download, Loader2, CheckCircle2, AlertCircle,
  Copy, Eye, Code, Image as ImageIcon, Hash, Link as LinkIcon, Sparkles, Tag,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { api } from '@/services/client';
import { mapAnalysisPayload, type PageInsight } from './contentAnalysisUtils';

interface ExtractedContent {
  url: string;
  title: string;
  content: string;
  wordCount: number;
  images: string[];
  links: string[];
  metadata: {
    author?: string;
    publishDate?: string;
    description?: string;
  };
  extractedAt: string;
}

export const ContentExtractionPage = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [extractedContent, setExtractedContent] = useState<ExtractedContent | null>(null);
  const [pageInsight, setPageInsight] = useState<PageInsight | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'formatted' | 'raw'>('formatted');

  const handleExtract = async () => {
    if (!url.trim()) {
      setError('Please enter a valid URL');
      return;
    }

    setLoading(true);
    setError(null);
    setPageInsight(null);

    try {
      const response = await api.post('/content/extract', { url }, { timeout: 120000 });
      const raw = response.data as Record<string, unknown>;
      const parsed: ExtractedContent = {
        url: String(raw.url ?? url),
        title: String(raw.title ?? 'Untitled'),
        content: String(raw.content ?? raw.text ?? ''),
        wordCount: Number(raw.wordCount ?? raw.word_count ?? 0),
        images: Array.isArray(raw.images) ? raw.images.map(String) : [],
        links: Array.isArray(raw.links) ? raw.links.map(String) : [],
        metadata: {
          author: raw.metadata && typeof raw.metadata === 'object'
            ? String((raw.metadata as Record<string, unknown>).author ?? '') || undefined
            : undefined,
          publishDate: raw.metadata && typeof raw.metadata === 'object'
            ? String((raw.metadata as Record<string, unknown>).publishDate ?? (raw.metadata as Record<string, unknown>).publish_date ?? '') || undefined
            : undefined,
          description: raw.metadata && typeof raw.metadata === 'object'
            ? String((raw.metadata as Record<string, unknown>).description ?? '') || undefined
            : undefined,
        },
        extractedAt: String(raw.extractedAt ?? raw.extracted_at ?? new Date().toISOString()),
      };
      setExtractedContent(parsed);

      if (parsed.content.trim()) {
        try {
          const analysisRes = await api.post('/content/analyze', {
            url: parsed.url,
            title: parsed.title,
            content: parsed.content,
          }, { timeout: 60000 });
          setPageInsight(mapAnalysisPayload(
            parsed.url,
            parsed.title,
            parsed.url,
            (analysisRes.data ?? {}) as Record<string, unknown>,
          ));
        } catch (analyzeErr) {
          console.warn('Insight generation failed:', analyzeErr);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Extraction failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const downloadContent = () => {
    if (!extractedContent) return;

    const blob = new Blob([extractedContent.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${extractedContent.title.replace(/[^a-z0-9]/gi, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <PageLayout
      title="Content Extraction"
      description="Extract clean, structured content from any webpage"
      icon={Globe}
    >
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl shadow-lg p-8 border border-khiimori-border/10"
        >
          {/* URL Input */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-khiimori-text-muted" />
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleExtract()}
                placeholder="Enter URL to extract content from..."
                className="w-full pl-12 pr-4 py-4 border-2 border-khiimori-border/10 rounded-xl focus:border-primary-500 focus:outline-none transition-colors text-lg bg-transparent text-khiimori-text-primary placeholder:text-khiimori-text-muted"
              />
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleExtract}
              disabled={loading}
              className="px-8 py-4 bg-gradient-primary text-white rounded-xl font-semibold transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Extracting...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  Extract
                </>
              )}
            </motion.button>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}
        </motion.div>

        {/* Extracted Content */}
        {extractedContent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Metadata */}
            <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl shadow-lg p-6 border border-khiimori-border/10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-khiimori-text-primary flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-success-500" />
                  Extraction Complete
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode(viewMode === 'formatted' ? 'raw' : 'formatted')}
                    className="px-4 py-2 border border-khiimori-border/10 rounded-lg hover:bg-khiimori-bg-elevated/10 transition-colors flex items-center gap-2 text-khiimori-text-secondary"
                  >
                    {viewMode === 'formatted' ? <Code className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {viewMode === 'formatted' ? 'Raw' : 'Formatted'}
                  </button>
                  <button
                    onClick={() => copyToClipboard(extractedContent.content)}
                    className="px-4 py-2 border border-khiimori-border/10 rounded-lg hover:bg-khiimori-bg-elevated/10 transition-colors flex items-center gap-2 text-khiimori-text-secondary"
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </button>
                  <button
                    onClick={downloadContent}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-primary-500/20 rounded-xl">
                  <div className="text-primary-400 text-sm font-medium mb-1">Words</div>
                  <div className="text-2xl font-bold text-primary-300">
                    {extractedContent.wordCount.toLocaleString()}
                  </div>
                </div>
                <div className="p-4 bg-success-500/20 rounded-xl">
                  <div className="text-success-400 text-sm font-medium mb-1">Images</div>
                  <div className="text-2xl font-bold text-success-300">
                    {extractedContent.images.length}
                  </div>
                </div>
                <div className="p-4 bg-purple-500/20 rounded-xl">
                  <div className="text-purple-400 text-sm font-medium mb-1">Links</div>
                  <div className="text-2xl font-bold text-purple-300">
                    {extractedContent.links.length}
                  </div>
                </div>
                <div className="p-4 bg-orange-500/20 rounded-xl">
                  <div className="text-orange-400 text-sm font-medium mb-1">Extracted</div>
                  <div className="text-sm font-bold text-orange-300">
                    {new Date(extractedContent.extractedAt).toLocaleTimeString()}
                  </div>
                </div>
              </div>

              {/* Title & Metadata */}
              <div className="space-y-3 border-t border-khiimori-border/10 pt-4">
                <div>
                  <div className="text-sm text-khiimori-text-muted mb-1">Title</div>
                  <div className="text-lg font-semibold text-khiimori-text-primary">{extractedContent.title}</div>
                </div>
                {extractedContent.metadata.author && (
                  <div>
                    <div className="text-sm text-khiimori-text-muted mb-1">Author</div>
                    <div className="text-khiimori-text-primary">{extractedContent.metadata.author}</div>
                  </div>
                )}
                {extractedContent.metadata.publishDate && (
                  <div>
                    <div className="text-sm text-khiimori-text-muted mb-1">Published</div>
                    <div className="text-khiimori-text-primary">{extractedContent.metadata.publishDate}</div>
                  </div>
                )}
                {extractedContent.metadata.description && (
                  <div>
                    <div className="text-sm text-khiimori-text-muted mb-1">Description</div>
                    <div className="text-khiimori-text-primary">{extractedContent.metadata.description}</div>
                  </div>
                )}
              </div>
            </div>

            {pageInsight && (pageInsight.summary || pageInsight.keyInsights.length > 0) && (
              <div className="bg-primary-500/10 border border-primary-500/20 rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-primary-400 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  AI Insights
                  <span className="text-xs font-normal text-khiimori-text-muted ml-2">
                    {pageInsight.confidence}% confidence · {pageInsight.category}
                  </span>
                </h3>
                {pageInsight.summary && (
                  <p className="text-sm text-khiimori-text-secondary leading-relaxed mb-4">{pageInsight.summary}</p>
                )}
                {pageInsight.keyInsights.length > 0 && (
                  <ul className="space-y-2 mb-4">
                    {pageInsight.keyInsights.map((line, i) => (
                      <li key={i} className="text-sm text-khiimori-text-secondary flex gap-2">
                        <span className="text-primary-400">•</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {pageInsight.topics.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <Tag className="h-4 w-4 text-khiimori-text-muted" />
                    {pageInsight.topics.map((topic) => (
                      <span key={topic} className="px-2 py-1 text-xs rounded-full bg-khiimori-bg-elevated/20 text-khiimori-text-muted">
                        {topic}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Content Display */}
            <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl shadow-lg p-6 border border-khiimori-border/10">
              <h3 className="text-lg font-bold text-khiimori-text-primary mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Extracted Content
              </h3>
              <div className={`prose max-w-none ${viewMode === 'raw' ? 'font-mono text-sm bg-khiimori-bg-elevated/10 p-4 rounded-lg overflow-auto' : ''}`}>
                {viewMode === 'formatted' ? (
                  <div className="whitespace-pre-wrap text-khiimori-text-secondary leading-relaxed">
                    {extractedContent.content}
                  </div>
                ) : (
                  <pre className="whitespace-pre-wrap text-khiimori-text-secondary">{extractedContent.content}</pre>
                )}
              </div>
            </div>

            {/* Images */}
            {extractedContent.images.length > 0 && (
              <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl shadow-lg p-6 border border-khiimori-border/10">
                <h3 className="text-lg font-bold text-khiimori-text-primary mb-4 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Images ({extractedContent.images.length})
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {extractedContent.images.slice(0, 8).map((img, idx) => (
                    <div key={idx} className="aspect-video bg-khiimori-bg-elevated/10 rounded-lg overflow-hidden">
                      <img src={img} alt={`Image ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
                {extractedContent.images.length > 8 && (
                  <div className="mt-4 text-center text-khiimori-text-muted text-sm">
                    And {extractedContent.images.length - 8} more images...
                  </div>
                )}
              </div>
            )}

            {/* Links */}
            {extractedContent.links.length > 0 && (
              <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl shadow-lg p-6 border border-khiimori-border/10">
                <h3 className="text-lg font-bold text-khiimori-text-primary mb-4 flex items-center gap-2">
                  <Hash className="w-5 h-5" />
                  Links ({extractedContent.links.length})
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {extractedContent.links.slice(0, 20).map((link, idx) => (
                    <a
                      key={idx}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-3 bg-khiimori-bg-elevated/10 rounded-lg hover:bg-khiimori-bg-elevated/20 transition-colors text-sm text-primary-400 hover:text-primary-300 truncate"
                    >
                      {link}
                    </a>
                  ))}
                </div>
                {extractedContent.links.length > 20 && (
                  <div className="mt-4 text-center text-khiimori-text-muted text-sm">
                    And {extractedContent.links.length - 20} more links...
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* Empty State */}
        {!extractedContent && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl shadow-lg p-12 border border-khiimori-border/10 text-center"
          >
            <Globe className="w-16 h-16 text-khiimori-text-muted mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-khiimori-text-primary mb-2">
              Ready to Extract Content
            </h3>
            <p className="text-khiimori-text-secondary max-w-md mx-auto">
              Enter a URL above to extract clean, structured content from any webpage.
              Perfect for research, archiving, or content analysis.
            </p>
          </motion.div>
        )}
      </div>
    </PageLayout>
  );
};
